import React from 'react';

import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  makeStyles,
  Paper,
  Zoom,
} from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';

import { MensagemInterface } from 'store/reducers/mensagens';
import { ContatoMencionado } from 'types';
import { whatsappToHtml } from 'utils/whatsappToHtml';

import { ChatInput, ChatInputRef, MentionItem } from './ChatInput';
import DialogHeader from './DialogHeader';
import EmojiButton from './EmojiButton';
import TextMessage from './TextMessage';

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    backgroundColor: palette.background.default,
    marginTop: spacing(3),
    marginBottom: spacing(1.5),
    '& > *': {
      margin: spacing(0, 1),
    },
  },
  inputContainer: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 2),
    borderRadius: spacing(3),
    backgroundColor: palette.background.paper,
    cursor: 'text',
  },
}));

type ModalEditarMensagemProps = {
  open: boolean;
  onClose: () => void;
  mensagem: MensagemInterface;
  handleEditarMensagem: (
    mensagem: MensagemInterface,
    newText: string,
    contatosMencionados?: ContatoMencionado[],
  ) => void;
  fetchUsers?: (query: string) => Promise<MentionItem[]>;
};

export default function ModalEditarMensagem(props: ModalEditarMensagemProps) {
  const { open, onClose, mensagem, handleEditarMensagem, fetchUsers } = props;
  const classes = useStyles();

  const [inputValue, setInputValue] = React.useState(mensagem.mensagem || '');
  const [mentions, setMentions] = React.useState<ContatoMencionado[]>([]);

  const chatInputRef = React.useRef<ChatInputRef>(null);

  const fallbackFetchUsers = React.useCallback(async () => [], []);

  const getInitialValue = React.useCallback(() => {
    if (!mensagem.mensagem) return '';
    let html = whatsappToHtml(mensagem.mensagem);

    if (mensagem.contatosMencionados?.length) {
      mensagem.contatosMencionados.forEach(contato => {
        const alias = `@${contato.contatoNome || contato.waPhoneNumberId}`;
        const regex = new RegExp(`@${contato.waPhoneNumberId}`, 'g');
        const span = `<span data-type="mention" data-id="${
          contato.contatoId
        }" data-label="${
          contato.contatoNome || contato.waPhoneNumberId
        }" data-wa-phone-number-id="${contato.waPhoneNumberId}" data-contact="${
          contato.waPhoneNumberId
        }">${alias}</span>`;
        html = html.replace(regex, span);
      });
    }
    return html;
  }, [mensagem.mensagem, mensagem.contatosMencionados]);

  const canEdit = React.useMemo(() => {
    return inputValue.length > 0 && inputValue !== mensagem.mensagem;
  }, [inputValue, mensagem.mensagem]);

  React.useEffect(() => {
    if (open) {
      setInputValue(mensagem.mensagem || '');
      setMentions([]);
    }
  }, [open, mensagem.mensagem]);

  const handleInputChange = (
    text: string,
    _hasContent: boolean,
    newMentions?: ContatoMencionado[],
  ) => {
    setInputValue(text);
    if (newMentions) setMentions(newMentions);
  };

  const handleSelectEmoji = (emoji: string) => {
    chatInputRef.current?.insertContent(emoji);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open}>
      <DialogHeader
        title="Editar Mensagem"
        description="Modifique o conteúdo da mensagem e confirme as alterações"
        onClose={onClose}
      />

      <DialogContent>
        <TextMessage fromMe mensagem={mensagem} />

        <Paper className={classes.root} elevation={0}>
          <EmojiButton onSelect={handleSelectEmoji} />

          <Box
            className={classes.inputContainer}
            onClick={() => chatInputRef.current?.focus()}
          >
            <ChatInput
              ref={chatInputRef}
              initialValue={getInitialValue()}
              fetchUsers={fetchUsers || fallbackFetchUsers}
              disableMentions={!fetchUsers}
              onSend={(text, ment) => {
                if (!canEdit) return;
                handleEditarMensagem(mensagem, text, ment);
              }}
              onInputChange={handleInputChange}
              placeholder="Escreva uma mensagem..."
              autoFocus
            />

            <Zoom in={inputValue.length > 0}>
              <ButtonBase
                onClick={() => {
                  setInputValue('');
                  chatInputRef.current?.clear();
                }}
                disableRipple
              >
                <CloseIcon />
              </ButtonBase>
            </Zoom>
          </Box>

          <ButtonBase
            disabled={!canEdit}
            onClick={() => handleEditarMensagem(mensagem, inputValue, mentions)}
            disableRipple
          >
            <CheckCircle color={canEdit ? 'primary' : 'disabled'} />
          </ButtonBase>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}
